import React, { useState, useEffect, useRef } from 'react';
import { createTableColumns } from '../utilities/createTableColumns';
import createExpandRowFormatter from '../utilities/createExpandRowFormatter';
import { useLinkedData } from '../hooks/useLinkedData';
import 'react-tabulator/lib/styles.css';
import 'react-tabulator/css/tabulator_bootstrap5.min.css';
import { ReactTabulator } from 'react-tabulator';



/**
 * VIEWER TABLE WITH TOGGLE ROW FUNCTIONALITY
 * 
 * This component displays asset data in a viewer extension context with
 * support for expandable rows that show additional data from external sources.
 * 
 * KEY FEATURES:
 * - Uses global data from window.assetDataProps (populated by AssetTable)
 * - Implements local toggle functionality using useLinkedData hooks
 * - Supports both Excel and Airtable data sources
 * - Creates custom expand formatters that work independently from main table
 * - Polls for data updates to stay synchronized with main AssetTable
 * 
 * TOGGLE ROW IMPLEMENTATION:
 * - Creates separate toggle functions for Excel and Airtable
 * - Updates Excel options dynamically when global data changes
 * - Exposes toggle functions back to global scope for cross-component access
 * - Uses custom expand formatter that calls local toggle functions
 * 
 * DATA FLOW:
 * 1. Polls window.assetDataProps for data updates from AssetTable
 * 2. Creates local toggle functions using useLinkedData hooks
 * 3. Exposes these functions globally for potential use by other components
 * 4. Renders table with conditional expand column based on asset mode
 */
const ViewerTableContent = () => {
  const [globalData, setGlobalData] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(true);
  const tableRef = useRef(null);
  
  /**
   * TOGGLE ROW FUNCTIONALITY SETUP
   * 
   * Initializes toggle functionality for both Excel and Airtable data sources.
   * The Excel hook requires dynamic options that update when global data changes,
   * while Airtable uses static configuration.
   * 
   * Excel Integration:
   * - Creates state for Excel options (data and link column)
   * - Updates options when global data changes to stay synchronized
   * - Uses these options to create the Excel toggle function
   * 
   * Airtable Integration:
   * - Uses static configuration for Airtable service
   * - No additional options required
   */
  const { toggleRow: toggleRowAirtable } = useLinkedData('airtable');
  
  // Create Excel options that update with global data
  const [excelOptions, setExcelOptions] = useState({
    excelData: [],
    linkColumn: null
  });
  
  // Update Excel options when global data changes
  useEffect(() => {
    const newOptions = {
      excelData: window.assetDataProps?.excelRows || [],
      linkColumn: window.assetDataProps?.selectedExcelColumn
    };
    setExcelOptions(newOptions);
  }, [globalData]);
  
  const { toggleRow: toggleRowExcel } = useLinkedData('excel', excelOptions);
  
  // Always use global data polling
  useEffect(() => {
    const updateGlobalData = () => {
      const selectedAssetMode = window.assetDataProps?.selectedAssetMode;
      let currentData = window.assetDataProps?.unfilteredTableData;

      if (selectedAssetMode && (selectedAssetMode === 'link2Excel' || selectedAssetMode === 'link2AirTable')) {
        currentData = window.assetDataProps?.linkedTableData || window.assetDataProps?.unfilteredTableData;
      }

      if (currentData) {
        setGlobalData(currentData);
        setGlobalLoading(false);
        console.log('ViewerTable: Data received', currentData);
      } else {
        setGlobalLoading(true);
        console.log('ViewerTable: No data received');
      }
    };
    
    // Initial update
    updateGlobalData();
    
    // Set up polling to check for data updates
    const interval = setInterval(updateGlobalData, 1000);
    
    return () => clearInterval(interval);
  }, []);

  /**
   * GLOBAL FUNCTION EXPOSURE
   * 
   * Exposes ViewerTable's toggle functions to the global scope so they can
   * be accessed by other components or extensions if needed. This creates
   * a bidirectional data flow where ViewerTable can both consume and provide
   * functionality through the global window object.
   */
  useEffect(() => {
    if (window.assetDataProps) {
      window.assetDataProps.viewerToggleRowExcel = toggleRowExcel;
      window.assetDataProps.viewerToggleRowAirtable = toggleRowAirtable;
      console.log('ViewerTable: Toggle functions exposed to global scope');
    }
  }, [toggleRowExcel, toggleRowAirtable]);


  
  // Use global data
  const data = globalData;
  const isLoading = globalLoading;
  const error = null;

  if (isLoading) {
    return <div>Loading asset data for ViewerTable...</div>;
  }

  if (error) {
    return <div>Error loading asset data: {error.message}</div>;
  }

  // Determine if we should show the expand column based on the asset mode
  const selectedAssetMode = window.assetDataProps?.selectedAssetMode;
  const includeExpandColumn =
    selectedAssetMode === 'link2Excel' || selectedAssetMode === 'link2AirTable';

  /**
   * CUSTOM EXPAND FORMATTER FOR VIEWER TABLE
   * 
   * Creates a custom expand button formatter specifically for ViewerTable
   * that uses the local toggle functions instead of relying on global ones.
   * This ensures that ViewerTable has independent toggle functionality
   * that works even if the main AssetTable's functions are not available.
   * 
   * Button Behavior:
   * - Shows right-pointing triangle when collapsed
   * - Shows down-pointing triangle when expanded
   * - Calls appropriate toggle function based on selected asset mode
   * - Handles both Excel and Airtable data sources
   * 
   * This approach provides better isolation and reliability compared to
   * using the shared expand formatter from createExpandRowFormatter.
   */
  const createViewerExpandFormatter = () => {
    return (cell, formatterParams) => {
      const button = document.createElement("button");
      button.innerHTML = "&#128898;"; // Right-pointing triangle character
      button.style.background = "none";
      button.style.border = "none";
      button.style.cursor = "pointer";
      button.setAttribute("data-expanded", "false");

      button.onclick = () => {
        const selectedAssetMode = window.assetDataProps?.selectedAssetMode;
        console.log('ViewerTable expand button clicked - selectedAssetMode:', selectedAssetMode);

        const isExpanded = button.getAttribute("data-expanded") === "true";
        if (isExpanded) {
          button.innerHTML = "&#128898;"; // Right-pointing triangle
          button.setAttribute("data-expanded", "false");
        } else {
          button.innerHTML = "&#128899;"; // Down-pointing triangle
          button.setAttribute("data-expanded", "true");
        }
        
        const row = cell.getRow();
        if (selectedAssetMode === 'link2Excel') {
          console.log('ViewerTable: Calling toggleRowExcel with row:', row.getData());
          toggleRowExcel(row);
        } else if (selectedAssetMode === 'link2AirTable') {
          console.log('ViewerTable: Calling toggleRowAirtable with row:', row.getData());
          toggleRowAirtable(row);
        } else {
          console.log('ViewerTable: No matching asset mode for toggle');
        }
      };

      return button;
    };
  };

  const expandRow = includeExpandColumn ? createViewerExpandFormatter() : null;

  // Create columns for the extension table
  const columns = data && data.length > 0 ? createTableColumns(
    data,
    window.assetDataProps?.customPropsMap || {},
    window.assetDataProps?.statusOptions || {},
    window.assetDataProps?.locationsMap || {},
    window.assetDataProps?.categoryOptions || {},
    {
      includeExpandColumn,
      expandFormatter: includeExpandColumn ? expandRow : null,
      headerContextMenu: window.assetDataProps?.headerContextMenu || [],
      editableRowIndex: null,
      disableSorting: true, // Disable sorting for extension
      categoryCustomAttributes: window.assetDataProps?.categoryCustomAttributes || {},
      disableHeaderFilters: true,
    }
  ) : [];

  // Tabulator options for the extension (with grouping and different settings)
  const options = {
    layout: 'fitColumns',
    tooltips: false, // Disable tooltips for cleaner look
    pagination: false, // Disable pagination for extension
    movableColumns: false, // Disable column moving for cleaner look
    headerSort: false, // Disable sorting
    resizableRows: false,
    height: '100%',

    // Enable grouping functionality
    groupBy: false, // Can be set to a field name to group by that field
    groupStartOpen: true,
    groupHeader: function(value, count, data){
      return value + " <span style='color:#d00; margin-left:10px;'>(" + count + " items)</span>";
    },
    
    // Enable row selection for better UX with expand functionality
    selectable: false, // Disable selection to avoid conflicts with expand
    
    // Different styling for the extension - more compact
    // rowHeight: 22,
    // headerHeight: 28,
    
    // Remove borders and styling for seamless integration
    cssClass: 'extension-table-borderless',

    // Add row click handler for debugging
    rowClick: function(e, row) {
      console.log('ViewerTable row clicked:', row.getData());
    }
  };

  return (
    <div style={{
      height: '100%',
      overflow: 'hidden',
      backgroundColor: 'white'
    }}>

      {data && data.length > 0 ? (
        <div style={{ height: '100%', color: 'black' }}>
          <ReactTabulator
            ref={tableRef}
            data={data}
            columns={columns}
            options={options}
            layout={'fitData'}
          />
        </div>
      ) : (
        <div style={{ 
          padding: '20px',
          textAlign: 'center', 
          color: '#888'
        }}>
          No data available
        </div>
      )}
    </div>
  );
};

// Main component - simplified to always use global data
const ViewerTable = () => {
  return <ViewerTableContent />;
};

export default ViewerTable;